import { useEffect, useRef, useState } from "react"
import { ArrowRight, Upload, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { getUploadDisplayUrl, revokeLocalObjectUrl, type UploadablePhoto } from "@/lib/vehicleStorage"

interface PhotoUploadPrincipalProps {
  photo: UploadablePhoto | string | null
  onPhotoUpdate: (photo: UploadablePhoto | string | null) => void
  coverPhoto?: string | null
  onReuseCoverPhoto?: () => Promise<void> | void
  categoryLabel?: string
  disabled?: boolean
  id?: string
}

const getFriendlyAngleLabel = (categoryLabel: string) => {
  const normalized = categoryLabel.trim().toLowerCase()

  const angleMap: Record<string, string> = {
    frontal: "da frente",
    traseira: "de trás",
    "lateral direita": "da lateral direita",
    "lateral esquerda": "da lateral esquerda",
    motor: "do motor",
    "painel e instrumentos": "do painel",
    interior: "do interior",
    "detalhes extras": "dos detalhes extras",
  }

  return angleMap[normalized] ?? `de ${normalized}`
}

const PhotoUploadPrincipal = ({
  photo,
  onPhotoUpdate,
  coverPhoto,
  onReuseCoverPhoto,
  categoryLabel = "da etapa",
  disabled = false,
  id = "photo-upload-principal",
}: PhotoUploadPrincipalProps) => {
  const [uploading, setUploading] = useState(false)
  const [reusingCoverPhoto, setReusingCoverPhoto] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const latestPhotoRef = useRef(photo)
  const friendlyAngleLabel = getFriendlyAngleLabel(categoryLabel)

  useEffect(() => {
    latestPhotoRef.current = photo
  }, [photo])

  useEffect(() => {
    return () => {
      revokeLocalObjectUrl(latestPhotoRef.current)
    }
  }, [])

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0]
      if (!file) return

      setUploading(true)
      revokeLocalObjectUrl(photo)
      onPhotoUpdate({
        url: URL.createObjectURL(file),
        file,
        originalName: file.name,
      })
      toast.success("Foto adicionada com sucesso")
    } catch (error) {
      console.error("Erro ao processar foto:", error)
      toast.error("Erro ao adicionar foto")
    } finally {
      setUploading(false)
      event.target.value = ""
    }
  }

  const removePhoto = () => {
    revokeLocalObjectUrl(photo)
    onPhotoUpdate("")
  }

  const handleReuseCoverPhoto = async () => {
    if (!onReuseCoverPhoto) return

    try {
      setReusingCoverPhoto(true)
      await onReuseCoverPhoto()
    } finally {
      setReusingCoverPhoto(false)
    }
  }

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        id={id}
        accept="image/*"
        capture="environment"
        onChange={handleFileUpload}
        disabled={uploading || disabled}
        className="hidden"
      />

      {photo ? (
        <div className="rounded-[1rem] border border-border/70 bg-background p-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
            <div className="relative w-full max-w-[320px]">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Foto da avaliação
              </p>
              <img
                src={getUploadDisplayUrl(photo)}
                alt="Foto da avaliação"
                className="h-[220px] w-full rounded-[1rem] border object-cover"
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute right-3 top-10 h-7 w-7 rounded-full"
                onClick={removePhoto}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>

            <div className="flex-1 space-y-4">
              <div>
                <p className="text-sm font-semibold text-foreground">Etapa concluída</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Esta foto agora é a referência de {categoryLabel.toLowerCase()} para esta avaliação.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  type="button"
                  variant="outline"
                  disabled={uploading || disabled}
                  onClick={() => inputRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {uploading ? "Enviando..." : "Trocar foto"}
                </Button>

              </div>
            </div>
          </div>
        </div>
      ) : coverPhoto && onReuseCoverPhoto ? (
        <div className="overflow-hidden rounded-[1rem] border border-border/70 bg-background shadow-sm">
          <div className="grid gap-0 lg:grid-cols-[minmax(0,260px)_1fr]">
            <div className="bg-muted/40 p-4">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Foto de capa cadastrada
              </p>
              <img
                src={coverPhoto}
                alt="Foto de capa do veículo"
                className="h-[190px] w-full rounded-[1rem] border object-cover"
              />
            </div>
            <div className="flex flex-col justify-between gap-4 p-4">
              <div className="space-y-2">
                <p className="text-sm font-semibold text-foreground">Reutilizar foto existente</p>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Você já tem uma foto frontal do veículo. Pode reutilizá-la nesta entrada ou tirar uma nova foto para registrar o estado atual.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  type="button"
                  onClick={handleReuseCoverPhoto}
                  disabled={uploading || reusingCoverPhoto || disabled}
                >
                  {reusingCoverPhoto ? "Usando..." : "Usar foto de capa"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => inputRef.current?.click()}
                  disabled={uploading || reusingCoverPhoto || disabled}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {uploading ? "Enviando..." : "Tirar nova foto"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[1rem] border-2 border-dashed border-primary/45 bg-primary/5 shadow-[0_14px_34px_hsl(212_72%_43%_/_0.10)]">
          <button
            type="button"
            disabled={uploading || disabled}
            onClick={() => inputRef.current?.click()}
            className="flex w-full flex-col gap-4 p-5 text-left transition-colors hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-60 lg:flex-row lg:items-center lg:justify-between"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[1rem] bg-primary text-primary-foreground shadow-lg shadow-primary/20">
                <Upload className="h-7 w-7" />
              </div>
              <div className="space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                  Primeiro passo
                </p>
                <p className="text-base font-semibold text-foreground">
                  Tire ou envie a foto atual {friendlyAngleLabel}
                </p>
                <p className="text-sm text-muted-foreground">
                  Esta é a primeira ação desta etapa. Depois dela, os critérios serão liberados.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20">
              <span>{uploading ? "Enviando..." : "Adicionar foto"}</span>
              <ArrowRight className="h-4 w-4" />
            </div>
          </button>
        </div>
      )}
    </div>
  )
}

export default PhotoUploadPrincipal
